import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Modal, Segmented, Spin, Typography} from 'antd';
import {CandlestickData} from '../../pages/trading/components/CandlestickChart';
import {klineBarsFromCandles} from '../charts/KLineChart';
import LightweightCandlestickChart from '../charts/LightweightCandlestickChart';
import {TechnicalIndicatorData, tradingService} from '../../services/tradingService';
import type {IndicatorConfig, TechnicalIndicator, TimeFrame} from '../../hooks/useChartState';
import CompactIndicatorBar from '../../pages/trading/components/CompactIndicatorBar';
import '../../pages/trading/components/CompactIndicatorBar.css';

const {Text} = Typography;

const normalizeTimestampMs = (value: unknown): number | undefined => {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num) || num <= 0) return undefined;
    if (num < 1_000_000_000_000) return Math.floor(num * 1000);
    return Math.floor(num);
};

const normalizeNumber = (value: unknown): number | undefined => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'string' && value.trim() === '') return undefined;
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return undefined;
    return num;
};

const transformIndicatorsForChart = (rawIndicators: Record<string, any> | undefined) => {
    if (!rawIndicators) return undefined;

    const result: Record<string, Array<{
        time: number;
        value?: number;
        upper?: number;
        middle?: number;
        lower?: number;
        diff?: number;
        signal?: number;
        histogram?: number;
        k?: number;
        d?: number;
        j?: number;
    }>> = {};

    Object.entries(rawIndicators).forEach(([key, indicator]) => {
        const values = indicator?.data?.values || indicator?.values;
        if (!values || !Array.isArray(values)) return;

        values.forEach((v: any) => {
            const multiPeriod = v.multiPeriodValues;
            if (!multiPeriod || typeof multiPeriod !== 'object') return;

            const timestamp = normalizeTimestampMs(v.timestamp ?? v.time);
            if (!timestamp) return;

            Object.entries(multiPeriod).forEach(([periodKey, periodData]: [string, any]) => {
                if (periodData === null || periodData === undefined) return;
                const seriesKey = `${key}_${periodKey}`;

                if (key === 'MACD') {
                    if (typeof periodData !== 'object') return;
                    const diff = normalizeNumber(periodData.diff ?? periodData.dif);
                    const signal = normalizeNumber(periodData.dea ?? periodData.signal);
                    const histogram = normalizeNumber(periodData.macd ?? periodData.histogram);
                    if (diff === undefined && signal === undefined && histogram === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        diff,
                        signal,
                        histogram,
                    });
                } else if (key === 'BOLL') {
                    if (typeof periodData !== 'object') return;
                    const upper = normalizeNumber(periodData.upper);
                    const middle = normalizeNumber(periodData.middle);
                    const lower = normalizeNumber(periodData.lower);
                    if (upper === undefined && middle === undefined && lower === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        upper,
                        middle,
                        lower,
                    });
                } else if (key === 'KDJ') {
                    if (typeof periodData !== 'object') return;
                    const k = normalizeNumber(periodData.k);
                    const d = normalizeNumber(periodData.d);
                    const j = normalizeNumber(periodData.j);
                    if (k === undefined && d === undefined && j === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        k,
                        d,
                        j,
                    });
                } else {
                    const value = normalizeNumber(periodData);
                    if (value === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        value,
                    });
                }
            });
        });
    });

    Object.values(result).forEach(arr => arr.sort((a, b) => a.time - b.time));
    return result;
};

interface HistoryPositionChartModalProps {
    visible: boolean;
    onClose: () => void;
    position: {
        instId: string;
        posSide: string;
        ctime: number | string;
        utime: number | string;
        openAvgPx?: number | string;
        closeAvgPx?: number | string;
    };
    apiKeyId?: number;
}

/**
 * 历史仓位K线图弹窗组件
 * 显示历史仓位的K线图，标注开仓和平仓点位
 */
const HistoryPositionChartModal: React.FC<HistoryPositionChartModalProps> = ({
                                                                                 visible,
                                                                                 onClose,
                                                                                 position,
                                                                                 apiKeyId
                                                                             }) => {
    const [chartData, setChartData] = useState<CandlestickData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [markPrice, setMarkPrice] = useState<number | null>(null);
    const [currentTimeFrame, setCurrentTimeFrame] = useState<TimeFrame>('5m');
    const [currentLimit, setCurrentLimit] = useState<number>(100);
    const [currentAlignedOpenTime, setCurrentAlignedOpenTime] = useState<number | undefined>(undefined);
    const [currentAlignedCloseTime, setCurrentAlignedCloseTime] = useState<number | undefined>(undefined);
    const [indicators, setIndicators] = useState<Record<string, TechnicalIndicatorData> | undefined>(undefined);
    /** 栏中可见的指标类型（默认全部显示，通过+菜单取消勾选移除） */
    const [toolbarIndicatorTypes, setToolbarIndicatorTypes] = useState<TechnicalIndicator[]>(['EMA', 'RSI', 'BOLL', 'MACD', 'KDJ']);
    const [visibleIndicators, setVisibleIndicators] = useState<Record<string, boolean>>({
        /** EMA 12: 指数移动平均线，对近期价格更敏感，用于短期趋势分析 */
        'EMA_12': true,
        /** EMA 26: 较长周期的 EMA，用于辅助判断中短期趋势 */
        'EMA_26': true,
        /** RSI 9: 相对强弱指标，用于识别超买超卖状态 */
        'RSI_9': true,
        /** RSI 14: 标准周期的 RSI */
        'RSI_14': true,
        /** BOLL 20: 布林带，衡量价格波动及潜在支撑压力位 */
        'BOLL_20': true,
        /** MACD 12: 平滑异同移动平均线，动量指标 */
        'MACD_12_26_9': true,
        /** KDJ 9: 随机指标，周期 9 */
        'KDJ_9': true,
        /** KDJ 14: 随机指标，周期 14 */
        'KDJ_14': true,
        /** KDJ 21: 随机指标，周期 21 */
        'KDJ_21': true,
    });
    const [showAvgPx, setShowAvgPx] = useState<boolean>(true);
    const [showClosePx, setShowClosePx] = useState<boolean>(true);

    // 解析开仓价和平仓价
    const openPrice = position?.openAvgPx !== undefined && position?.openAvgPx !== null ? Number(position.openAvgPx) : undefined;
    const closePrice = position?.closeAvgPx !== undefined && position?.closeAvgPx !== null ? Number(position.closeAvgPx) : undefined;
    const bars = useMemo(() => klineBarsFromCandles(chartData), [chartData]);

    // 切换指标可见性
    const toggleIndicator = (key: string, checked: boolean) => {
        setVisibleIndicators(prev => ({
            ...prev,
            [key]: checked
        }));
    };

    // 获取K线数据
    useEffect(() => {
        if (visible && position && position.instId) {
            fetchChartData();
        }
    }, [visible, position, apiKeyId]);

    const fetchChartData = async () => {
        setLoading(true);
        setError('');

        try {
            // 计算持仓时长（毫秒）
            const openTime = normalizeTimestampMs(position.ctime);
            const closeTime = normalizeTimestampMs(position.utime);
            if (!openTime || !closeTime) {
                setError('缺少有效的开仓/平仓时间');
                setChartData([]);
                setIndicators(undefined);
                setMarkPrice(null);
                setCurrentAlignedOpenTime(undefined);
                setCurrentAlignedCloseTime(undefined);
                return;
            }

            const holdDuration = closeTime - openTime;
            if (holdDuration <= 0) {
                setError('开仓/平仓时间不合法');
                setChartData([]);
                setIndicators(undefined);
                setMarkPrice(null);
                setCurrentAlignedOpenTime(undefined);
                setCurrentAlignedCloseTime(undefined);
                return;
            }

            // 根据持仓时长选择时间帧
            const pickAutoTimeFrame = (durationMs: number): TimeFrame => {
                const hourMs = 60 * 60 * 1000;
                if (durationMs < 1 * hourMs) return '1m';
                if (durationMs < 4 * hourMs) return '5m';
                if (durationMs < 3 * 24 * hourMs) return '1h';
                if (durationMs < 12 * 24 * hourMs) return '4h';
                return '1d';
            };
            const timeframe: TimeFrame = pickAutoTimeFrame(holdDuration);

            // 时间帧对应的毫秒数
            const timeFrameMs: number = (() => {
                if (timeframe === '1m') return 60 * 1000;
                if (timeframe === '5m') return 5 * 60 * 1000;
                if (timeframe === '1h') return 60 * 60 * 1000;
                if (timeframe === '4h') return 4 * 60 * 60 * 1000;
                return 24 * 60 * 60 * 1000; // '1d'
            })();

            // 将开仓和平仓时间对齐到K线边界（K线开始时间）
            const openCandleStartTime = Math.floor(openTime / timeFrameMs) * timeFrameMs;
            const closeCandleStartTime = Math.floor(closeTime / timeFrameMs) * timeFrameMs;
            const holdingCandles = Math.max(1, Math.round((closeCandleStartTime - openCandleStartTime) / timeFrameMs));

            // 以100个K线为基准，自适应计算limit
            const WARMUP_CANDLES = 35;       // 指标预热所需K线数
            const AFTER_BUFFER = 5;           // 平仓后保留K线缓冲
            const MIN_CANDLES = 100;          // 基准K线数
            const MAX_CANDLES = 240;          // 后端接口limit上限

            const limit = Math.min(
                Math.max(MIN_CANDLES, holdingCandles + WARMUP_CANDLES + AFTER_BUFFER),
                MAX_CANDLES
            );

            // 居中计算：持仓前方的K线数 = 指标预热 + 居中余量
            const beforeCandles = Math.max(WARMUP_CANDLES, Math.floor((limit - holdingCandles) / 2));
            // 如果后方余量不足缓冲，向前方借位
            const afterCandles = limit - beforeCandles - holdingCandles;
            const finalBeforeCandles = afterCandles < AFTER_BUFFER
                ? Math.max(WARMUP_CANDLES, limit - holdingCandles - AFTER_BUFFER)
                : beforeCandles;

            // 计算startMills：从开仓前n个K线开始获取数据
            const finalStartMills = Math.max(0, openCandleStartTime - finalBeforeCandles * timeFrameMs);

            if (!Number.isFinite(finalStartMills)) {
                setError('K线时间窗计算失败');
                setChartData([]);
                setIndicators(undefined);
                setMarkPrice(null);
                setCurrentAlignedOpenTime(undefined);
                setCurrentAlignedCloseTime(undefined);
                return;
            }

            // 更新当前显示值
            setCurrentTimeFrame(timeframe);
            setCurrentLimit(limit);

            // 设置对齐后的开仓和平仓时间，用于图表显示标注
            const actualOpenTime = openCandleStartTime;
            const actualCloseTime = closeCandleStartTime;
            setCurrentAlignedOpenTime(actualOpenTime);
            setCurrentAlignedCloseTime(actualCloseTime);

            // 请求K线数据
            const response = await tradingService.getCompleteChartData({
                instId: position.instId,
                apiKeyId,
                timeframe: timeframe,
                limit,
                indicators: ['EMA', 'RSI', 'BOLL', 'MACD', 'KDJ'],
                emaPeriods: [12, 26],
                rsiPeriods: [9, 14],
                bollParams: ['20_2'],
                macdPeriods: [12, 26, 9],
                kdjPeriods: [9, 14, 21],
                startMills: finalStartMills,
            });

            // 处理响应数据
            const candles: CandlestickData[] = (response.candles || []).map((c) => ({
                timestamp: c.timestamp,
                open: c.open,
                high: c.high,
                low: c.low,
                close: c.close,
                volume: c.volume ?? 0,
                confirm: c.confirm ?? 1,
            }));

            const wrappedIndicators: Record<string, TechnicalIndicatorData> | undefined = response.indicators
                ? (Object.fromEntries(
                    Object.entries(response.indicators).map(([key, data]) => [
                        key,
                        {success: true, message: '', data},
                    ])
                ) as Record<string, TechnicalIndicatorData>)
                : undefined;

            setChartData(candles);
            setMarkPrice(response.markPrice || null);
            setIndicators(wrappedIndicators);

            if (candles.length === 0) {
                setError('未获取到K线数据');
            }
        } catch (e) {
            // console.error('获取K线数据失败:', e);
            setError('获取K线数据失败');
        } finally {
            setLoading(false);
        }
    };

    /** 通过+菜单取消勾选可移除指标类型，但点击tag只切换可见性不删除 */
    const selectedIndicators: TechnicalIndicator[] = toolbarIndicatorTypes;

    const indicatorConfigs = useMemo<Record<string, IndicatorConfig>>(() => {
        const configs: Record<string, IndicatorConfig> = {};
        const INDICATOR_PERIODS: Record<string, { periods: number[]; colors: string[] }> = {
            EMA: {periods: [12, 26], colors: ['#4d9fff', '#00b4d8']},
            RSI: {periods: [9, 14], colors: ['#9254de', '#d46b08']},
            BOLL: {periods: [20], colors: ['#f5222d']},
            KDJ: {periods: [9, 14, 21], colors: ['#f5a623', '#13c2c2', '#eb2f96']},
        };

        (['EMA', 'RSI', 'BOLL', 'MACD', 'KDJ'] as const).forEach(type => {
            if ('MACD' === type) {
                const macdVisible = visibleIndicators['MACD_12_26_9'] !== false;
                configs[type] = {
                    period: macdVisible ? 12 : 0,
                    fastPeriod: macdVisible ? 12 : 0,
                    slowPeriod: macdVisible ? 26 : 0,
                    signalPeriod: macdVisible ? 9 : 0,
                    periods: [], visiblePeriods: macdVisible ? [1] : [],
                    periodColors: ['#fa541c'],
                };
                return;
            }
            const {periods, colors} = INDICATOR_PERIODS[type] || {periods: [], colors: []};
            const visiblePeriods = periods.filter(p => visibleIndicators[`${type}_${p}`] !== false);
            configs[type] = {period: periods[0] || 0, periods, visiblePeriods, periodColors: colors};
        });
        return configs;
    }, [visibleIndicators]);

    const handleCompactToggleIndicator = useCallback((type: TechnicalIndicator) => {
        setVisibleIndicators(prev => {
            const next = {...prev};
            if ('MACD' === type) {
                next['MACD_12_26_9'] = prev['MACD_12_26_9'] === false;
            } else {
                const prefix = type + '_';
                const keys = Object.keys(next).filter(k => k.startsWith(prefix));
                const anyVisible = keys.some(k => next[k] !== false);
                keys.forEach(k => {
                    next[k] = !anyVisible;
                });
            }
            return next;
        });
    }, []);

    /** +菜单取消勾选：从栏中移除指标类型并设为不可见；勾选则恢复 */
    const handleToggleFromPicker = useCallback((type: TechnicalIndicator) => {
        const prefix = type + '_';
        setToolbarIndicatorTypes(prev => {
            const exists = prev.includes(type);
            if (exists) {
                // 移除：设为不可见（同时覆盖 visibleIndicators 和 indicators 中所有匹配 key）
                if ('MACD' === type) {
                    setVisibleIndicators(prev => ({...prev, 'MACD_12_26_9': false}));
                } else {
                    setVisibleIndicators(prev => {
                        const next = {...prev};
                        const allKeys = new Set([...Object.keys(prev), ...Object.keys(indicators || {})]);
                        allKeys.forEach(k => {
                            if (k.startsWith(prefix)) next[k] = false;
                        });
                        return next;
                    });
                }
                return prev.filter(t => t !== type);
            }
            // 添加：设为可见
            if ('MACD' === type) {
                setVisibleIndicators(prev => ({...prev, 'MACD_12_26_9': true}));
            } else {
                setVisibleIndicators(prev => {
                    const next = {...prev};
                    const allKeys = new Set([...Object.keys(prev), ...Object.keys(indicators || {})]);
                    allKeys.forEach(k => {
                        if (k.startsWith(prefix)) next[k] = true;
                    });
                    return next;
                });
            }
            return [...prev, type];
        });
    }, [indicators]);

    const handleCompactUpdateConfig = useCallback((type: TechnicalIndicator, config: Partial<IndicatorConfig>) => {
        if (config.visiblePeriods) {
            setVisibleIndicators(prev => {
                const next = {...prev};
                if ('MACD' === type) {
                    next['MACD_12_26_9'] = config.visiblePeriods!.length > 0;
                } else {
                    const prefix = type + '_';
                    Object.keys(next).filter(k => k.startsWith(prefix)).forEach(k => {
                        const period = parseInt(k.split('_')[1], 10);
                        next[k] = config.visiblePeriods!.includes(period);
                    });
                }
                return next;
            });
        }
    }, []);

    // 稳定对象引用，避免父组件周期性重渲染导致图表 effect 重复触发
    const chartIndicators = useMemo(() => transformIndicatorsForChart(indicators), [indicators]);
    const chartReferenceLines = useMemo(() => ({
        avgPx: openPrice,
        closePx: closePrice,
        cTime: currentAlignedOpenTime,
        uTime: currentAlignedCloseTime,
        posSide: position?.posSide,
    }), [openPrice, closePrice, currentAlignedOpenTime, currentAlignedCloseTime, position?.posSide]);
    const chartCenterTimeRange = useMemo(() => currentAlignedOpenTime && currentAlignedCloseTime ? {
        from: currentAlignedOpenTime,
        to: currentAlignedCloseTime,
    } : undefined, [currentAlignedOpenTime, currentAlignedCloseTime]);

    return (
        <Modal
            title={
                position ? (
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <Text style={{color: position.posSide === 'long' ? '#00d97e' : '#ff5252'}}>
                            {position.instId} K线图
                        </Text>
                        <Text style={{color: '#8c8c8c', fontSize: 12}}>
                            {currentTimeFrame} · {currentLimit}
                        </Text>
                        <Segmented
                            size="small"
                            value={currentTimeFrame}
                            disabled
                            options={(['1m', '5m', '1h', '4h', '1d'] as TimeFrame[]).map(tf => ({
                                label: tf,
                                value: tf,
                            }))}
                            style={{marginLeft: 8}}
                        />
                        <Segmented
                            size="small"
                            value={currentLimit}
                            disabled
                            options={([100, 150, 200, 240] as number[]).map(l => ({
                                label: String(l),
                                value: l,
                            }))}
                        />
                        <Segmented
                            size="small"
                            value={showAvgPx ? 'show' : 'hide'}
                            options={[
                                {label: '开仓', value: 'show'},
                                {label: 'Hide', value: 'hide'},
                            ]}
                            onChange={(value) => setShowAvgPx(value === 'show')}
                            style={{marginLeft: 8}}
                        />
                        <Segmented
                            size="small"
                            value={showClosePx ? 'show' : 'hide'}
                            options={[
                                {label: '平仓', value: 'show'},
                                {label: 'Hide', value: 'hide'},
                            ]}
                            onChange={(value) => setShowClosePx(value === 'show')}
                            style={{marginLeft: 8}}
                        />
                    </div>
                ) : 'K线图'
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1350}
            styles={{
                body: {backgroundColor: '#0d1117'},
                header: {backgroundColor: '#0d1117', color: '#ffffff', borderBottom: '1px solid rgba(255,255,255,0.07)'}
            }}
            style={{color: '#ffffff'}}
        >
            {loading ? (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 600}}>
                    <Spin size="large"/>
                </div>
            ) : error ? (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 600}}>
                    <Text style={{color: '#ff5252'}}>{error}</Text>
                </div>
            ) : chartData.length > 0 && position ? (
                <div style={{padding: 16, display: 'flex', flexDirection: 'column', gap: 8}}>
                    <CompactIndicatorBar
                        selectedIndicators={selectedIndicators}
                        indicatorConfigs={indicatorConfigs}
                        onToggleIndicator={handleCompactToggleIndicator}
                        onUpdateIndicatorConfig={handleCompactUpdateConfig}
                        onToggleFromPicker={handleToggleFromPicker}
                        readonly={true}
                        availableTypes={['EMA', 'RSI', 'BOLL', 'MACD', 'KDJ']}
                    />
                    <div style={{width: '100%', height: 560}}>
                        <LightweightCandlestickChart
                            data={bars}
                            height={560}
                            maxVisibleBars={currentLimit}
                            timeFrame={currentTimeFrame}
                            indicators={chartIndicators}
                            visibleIndicators={visibleIndicators}
                            referenceLines={chartReferenceLines}
                            showEntryLine={false}
                            lockRightEdge={false}
                            disableHorizontalZoom={true}
                            centerTimeRange={chartCenterTimeRange}
                            showAvgPx={showAvgPx}
                            showClosePx={showClosePx}
                        />
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: 24,
                        fontSize: 12,
                        paddingTop: 4,
                        borderTop: '1px solid rgba(255,255,255,0.07)'
                    }}>
                        {openPrice && (
                            <Text style={{color: '#F0B429'}}>
                                开仓价: {openPrice.toFixed(2)}
                            </Text>
                        )}
                        {closePrice && (
                            <Text style={{color: '#00d97e'}}>
                                平仓价: {closePrice.toFixed(2)}
                            </Text>
                        )}
                        {position?.ctime && (
                            <Text style={{color: '#8c8c8c'}}>
                                开仓时间: {new Date(Number(position.ctime)).toLocaleString()}
                            </Text>
                        )}
                        {position?.utime && (
                            <Text style={{color: '#8c8c8c'}}>
                                平仓时间: {new Date(Number(position.utime)).toLocaleString()}
                            </Text>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 600}}>
                    <Text style={{color: '#8c8c8c'}}>暂无K线数据</Text>
                </div>
            )}
        </Modal>
    );
};

export default HistoryPositionChartModal;
